/**
 * 请求参数加密/解密
 * 客户端将 query params 加密为单一 token，服务端解密还原
 * 复用 SECURITY_ENCRYPT_KEY / SECURITY_IV（AES-256-CBC）
 */

import CryptoJS from "crypto-js";

const getKey = () => process.env.SECURITY_ENCRYPT_KEY || process.env.NEXT_PUBLIC_ENCRYPT_KEY || "";
const getIV = () => process.env.SECURITY_IV || process.env.NEXT_PUBLIC_ENCRYPT_IV || "";

function isCryptoAvailable(): boolean {
  const key = getKey();
  const iv = getIV();
  return !!(key && iv && key.length === 64 && iv.length === 32);
}

/**
 * 加密请求参数（客户端用）
 */
export function encodeRequestParams(params: Record<string, string | number | boolean>): string {
  if (!isCryptoAvailable() || Object.keys(params).length === 0) {
    // 未配置密钥时降级为明文拼接
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => sp.append(k, String(v)));
    return sp.toString();
  }

  const keyBytes = CryptoJS.enc.Hex.parse(getKey());
  const ivBytes = CryptoJS.enc.Hex.parse(getIV());
  const json = JSON.stringify(params);
  const encrypted = CryptoJS.AES.encrypt(json, keyBytes, {
    iv: ivBytes,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const sp = new URLSearchParams();
  sp.set("_e", encrypted.toString());
  return sp.toString();
}

/**
 * 解密请求参数（服务端用）
 * 从 URL searchParams 中提取并解密，同时合并明文参数（兼容降级）
 */
export function decodeRequestParams(url: URL): URLSearchParams {
  const encrypted = url.searchParams.get("_e");

  if (encrypted && isCryptoAvailable()) {
    try {
      const keyBytes = CryptoJS.enc.Hex.parse(getKey());
      const ivBytes = CryptoJS.enc.Hex.parse(getIV());
      const decrypted = CryptoJS.AES.decrypt(encrypted, keyBytes, {
        iv: ivBytes,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const json = decrypted.toString(CryptoJS.enc.Utf8);
      const params = JSON.parse(json) as Record<string, string | number | boolean>;
      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => sp.append(k, String(v)));
      return sp;
    } catch {
      // 解密失败，返回空（拒绝请求）
      return new URLSearchParams();
    }
  }

  // 降级：直接使用原始 searchParams
  return url.searchParams;
}

/**
 * 安全解析整数参数，带上下限钳制
 */
export function safeParseInt(value: string | null, defaultValue: number, min: number = 1, max: number = 100): number {
  if (!value) return defaultValue;
  const num = parseInt(value, 10);
  if (isNaN(num)) return defaultValue;
  return Math.max(min, Math.min(max, num));
}
